import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE, setUploadInProgress, hasPendingLogout, clearPendingLogout } from '../lib/api';
import { isNative, enableBackgroundMode, updateBackgroundProgress, disableBackgroundMode } from '../native/backgroundMode';
import { isNativeUploadAvailable, startNativeUpload, addNativeUploadListener } from '../native/nativeUpload';
import {
  enqueueUpload as queueEnqueue,
  getQueueItems,
  removeFromQueue,
  processQueue,
  setupNetworkListener,
  setQueueUploadFn,
  onQueueChange,
  getPendingCount,
  cleanupCompleted,
  clearAllFailed,
} from '../lib/uploadQueue';
import {
  getChunkTimeoutMs,
  getCompleteTimeoutMs,
  getOfflineWaitMs,
  getUploadRetryCount,
  getMaxConcurrentChunks,
} from '../lib/uploadNetwork';

// ── Fire-and-forget telemetry helpers ────────────────────────────────

function logUploadFailure({ stage, errorMessage, fileName, fileSizeBytes }) {
  try {
    axios.post(`${API_BASE}/upload-logs`, { stage, errorMessage, fileName, fileSizeBytes }, {
      timeout: 5000,
    }).catch(() => {});
  } catch {}
}

function logUploadTelemetry(data) {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    axios.post(`${API_BASE}/upload-telemetry`, {
      ...data,
      networkType: conn?.effectiveType || null,
    }, { timeout: 5000 }).catch(() => {});
  } catch {}
}

function logChunkTelemetry(chunks) {
  try {
    if (!chunks.length) return;
    axios.post(`${API_BASE}/chunk-telemetry`, { chunks }, {
      timeout: 10000,
    }).catch(() => {});
  } catch {}
}

// ── Concurrency limiter ──────────────────────────────────────────────

async function uploadChunksWithLimit(chunkJobs, concurrency, signal) {
  const results = new Array(chunkJobs.length);
  let nextIndex = 0;
  const runNext = async () => {
    while (nextIndex < chunkJobs.length) {
      if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      const idx = nextIndex++;
      results[idx] = await chunkJobs[idx]();
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, chunkJobs.length) }, () => runNext());
  await Promise.all(workers);
  return results;
}

// ── Retry init-upload on transient network errors ─────────────────────
// A single failure on the init call loses the whole upload, so retry
// network errors and 5xx the same way chunk uploads do. 4xx (auth,
// rate-limit, validation) are not retried — they'd fail the same way again.
async function postInitUploadWithRetry(url, body, opts, maxAttempts = 3) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await axios.post(url, body, opts);
    } catch (err) {
      lastErr = err;
      if (err.name === 'AbortError' || err.name === 'CanceledError') throw err;
      const status = err.response?.status;
      const retriable = !err.response || status >= 500 || err.code === 'ECONNABORTED';
      if (!retriable || attempt === maxAttempts) throw err;
      console.warn(`[upload] init-upload attempt ${attempt}/${maxAttempts} failed: ${err.message}, retrying...`);
      await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 1)));
    }
  }
  throw lastErr;
}

// ── Wait for connectivity ─────────────────────────────────────────────

async function waitForOnline(maxWaitMs) {
  if (navigator.onLine) return true;
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 2000));
    if (navigator.onLine) return true;
  }
  return false;
}

const UploadContext = createContext();

export const UploadProvider = ({ children }) => {
  const [upload, setUpload] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [queuePendingCount, setQueuePendingCount] = useState(0);
  const [queueUploading, setQueueUploading] = useState(false);
  const abortRef = useRef(null);
  const uploadStartTimeRef = useRef(null);
  const wakeLockRef = useRef(null);
  const uploadLockRef = useRef(false);

  // ── Queue state ──────────────────────────────────────────────────

  const refreshQueue = useCallback(async () => {
    try {
      const items = await getQueueItems();
      setQueueItems(items);
      setQueuePendingCount(await getPendingCount());
    } catch (e) {
      console.warn('Failed to refresh queue:', e.message);
    }
  }, []);

  // ── Page unload guard ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if (upload?.status === 'uploading' || queueUploading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [upload?.status, queueUploading]);

  // ── Wake lock (keep screen on during active upload) ──────────────

  const isUploading = upload?.status === 'uploading' || queueUploading;
  useEffect(() => {
    if (isUploading && !isNative() && navigator.wakeLock) {
      navigator.wakeLock.request('screen')
        .then(lock => { wakeLockRef.current = lock; })
        .catch(() => {});
    }
    return () => { wakeLockRef.current?.release().catch(() => {}); wakeLockRef.current = null; };
  }, [isUploading]);

  // ── App background warning (web only) ───────────────────────────

  useEffect(() => {
    if (!isUploading || isNative()) return;
    const handler = () => {
      if (document.hidden) toast.warning('Keep the app open until upload finishes!', { duration: 8000 });
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isUploading]);

  // ── Primary upload (called directly from RecordSession) ──────────

  const runUpload = useCallback(async (files, date, uploadId, durationSeconds, sessionType) => {
    // Prevent double-tap: if an upload is already running, ignore
    if (uploadLockRef.current) {
      console.warn('[upload] Upload already in progress, ignoring duplicate call');
      return;
    }
    uploadLockRef.current = true;

    const abortController = new AbortController();
    abortRef.current = abortController;
    uploadStartTimeRef.current = Date.now();
    setUploadInProgress(true);
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB (R2 multipart minimum)
    const native = isNative();

    if (native) {
      await enableBackgroundMode('Uploading...', `0% · ${files.length} file(s)`);
    }

    // Network-adaptive values — re-read at upload start
    const chunkTimeoutMs   = getChunkTimeoutMs();
    const maxConcurrent    = getMaxConcurrentChunks();
    const maxRetries       = getUploadRetryCount();
    const offlineWaitMs    = getOfflineWaitMs();
    const completeTimeout  = getCompleteTimeoutMs();

    let lastProgressFlush = 0;
    let pendingProgress = null;
    let progressTimer = null;
    const PROGRESS_INTERVAL = 500;
    let lastNotifUpdate = 0;
    const NOTIF_INTERVAL = 3000;

    const flushProgress = () => {
      if (pendingProgress !== null) {
        const { pct, eta } = pendingProgress;
        setUpload(prev => prev?.id === uploadId ? { ...prev, progress: pct, eta } : prev);
        pendingProgress = null;
        lastProgressFlush = Date.now();
      }
    };

    // Chunk telemetry accumulator
    const chunkTimings = [];

    try {
      const totalSize = files.reduce((sum, f) => sum + f.size, 0);

      const initRes = await postInitUploadWithRetry(`${API_BASE}/sessions/init-upload`, {
        date,
        duration_seconds: durationSeconds || 0,
        session_type: sessionType || 'evening_findings',
        files: files.map(f => ({
          name: f.name,
          type: f.type || (f.name?.includes('.') ? 'application/octet-stream' : 'audio/mpeg'),
          size: f.size,
        })),
      }, { timeout: 30000, signal: abortController.signal });

      const { sessionId, uploads } = initRes.data;

      if (!sessionId) {
        throw new Error('Server returned invalid response (missing sessionId). Please try again.');
      }

      // Server says session is already processing — no upload needed
      if (!uploads || uploads.length === 0) {
        setUpload(prev => prev?.id === uploadId ? { ...prev, status: 'complete', progress: 100, eta: null, sessionId } : prev);
        toast.success('Session is already being processed.');
        setUploadInProgress(false);
        if (native) await disableBackgroundMode(true, 'Upload complete!', 'Session already processing.');
        return;
      }

      if (!Array.isArray(uploads)) {
        throw new Error('Server returned invalid response (malformed uploads). Please try again.');
      }

      const chunkProgress = {};
      const completedUploads = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadInfo = uploads[i];
        if (!uploadInfo) {
          console.warn(`[upload] Server returned ${uploads.length} upload slots for ${files.length} files — skipping file ${i + 1} (${file.name})`);
          if (i === 0) throw new Error('Server returned no upload slots. Please try again.');
          break;
        }

        if (uploadInfo.directPut) {
          const progressKey = `${i}-direct`;
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            if (abortController.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
            const online = await waitForOnline(offlineWaitMs);
            if (!online) throw new Error('Connection lost during upload');

            const chunkStart = Date.now();
            try {
              await axios.post(
                `${API_BASE}/sessions/${sessionId}/direct-put`,
                file,
                {
                  headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Object-Key': uploadInfo.key,
                    'X-Content-Type': uploadInfo.contentType || file.type || 'application/octet-stream',
                  },
                  timeout: chunkTimeoutMs,
                  signal: abortController.signal,
                  onUploadProgress: (p) => {
                    chunkProgress[progressKey] = p.loaded;
                    const totalLoaded = Object.values(chunkProgress).reduce((s, v) => s + v, 0);
                    const pct = Math.min(99, Math.round((totalLoaded * 100) / totalSize));
                    const elapsed = (Date.now() - uploadStartTimeRef.current) / 1000;
                    const eta = (pct > 2 && elapsed > 3) ? Math.ceil((elapsed / pct) * (100 - pct)) : null;
                    pendingProgress = { pct, eta };
                    const now = Date.now();
                    if (now - lastProgressFlush >= PROGRESS_INTERVAL) {
                      clearTimeout(progressTimer);
                      flushProgress();
                    } else if (!progressTimer) {
                      progressTimer = setTimeout(() => { progressTimer = null; flushProgress(); }, PROGRESS_INTERVAL - (now - lastProgressFlush));
                    }
                    if (native && now - lastNotifUpdate >= NOTIF_INTERVAL) {
                      lastNotifUpdate = now;
                      const etaStr = eta > 60 ? `~${Math.ceil(eta / 60)}m` : eta > 0 ? `~${eta}s` : '';
                      updateBackgroundProgress('Uploading...', `${pct}%${etaStr ? ` · ${etaStr}` : ''}`, pct);
                    }
                  },
                }
              );
              chunkTimings.push({
                sessionId, uploadPath: 'direct-put', chunkIndex: 0,
                chunkSizeBytes: file.size, uploadDurationMs: Date.now() - chunkStart,
                attemptCount: attempt,
              });
              chunkProgress[progressKey] = file.size;
              break;
            } catch (err) {
              if (err.name === 'AbortError' || err.name === 'CanceledError') throw err;
              console.warn(`Direct PUT attempt ${attempt}/${maxRetries} failed:`, err.message);
              if (attempt === maxRetries) throw err;
              await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 1)));
            }
          }
          completedUploads.push({ key: uploadInfo.key, directPut: true });
          continue;
        }

        // ── Multipart ───────────────────────────────────────────────

        const totalParts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

        const chunkJobs = Array.from({ length: totalParts }, (_, idx) => {
          const partNum = idx + 1;
          const start = idx * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const progressKey = `${i}-${partNum}`;

          return async () => {
            if (abortController.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
            const chunk = file.slice(start, end);

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              if (abortController.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
              const online = await waitForOnline(offlineWaitMs);
              if (!online) throw new Error('Connection lost during upload');

              const chunkStart = Date.now();
              try {
                const partRes = await axios.post(
                  `${API_BASE}/sessions/${sessionId}/upload-part`,
                  chunk,
                  {
                    headers: {
                      'Content-Type': 'application/octet-stream',
                      'X-Object-Key': uploadInfo.key,
                      'X-Upload-Id': uploadInfo.uploadId,
                      'X-Part-Number': String(partNum),
                    },
                    timeout: chunkTimeoutMs,
                    signal: abortController.signal,
                    onUploadProgress: (p) => {
                      chunkProgress[progressKey] = p.loaded;
                      const totalLoaded = Object.values(chunkProgress).reduce((s, v) => s + v, 0);
                      const pct = Math.min(99, Math.round((totalLoaded * 100) / totalSize));
                      const elapsed = (Date.now() - uploadStartTimeRef.current) / 1000;
                      const eta = (pct > 2 && elapsed > 3) ? Math.ceil((elapsed / pct) * (100 - pct)) : null;
                      pendingProgress = { pct, eta };
                      const now = Date.now();
                      if (now - lastProgressFlush >= PROGRESS_INTERVAL) {
                        clearTimeout(progressTimer);
                        flushProgress();
                      } else if (!progressTimer) {
                        progressTimer = setTimeout(() => { progressTimer = null; flushProgress(); }, PROGRESS_INTERVAL - (now - lastProgressFlush));
                      }
                      if (native && now - lastNotifUpdate >= NOTIF_INTERVAL) {
                        lastNotifUpdate = now;
                        const etaStr = eta > 60 ? `~${Math.ceil(eta / 60)}m` : eta > 0 ? `~${eta}s` : '';
                        updateBackgroundProgress('Uploading...', `${pct}%${etaStr ? ` · ${etaStr}` : ''}`, pct);
                      }
                    },
                  }
                );
                chunkTimings.push({
                  sessionId, uploadPath: 'multipart', chunkIndex: idx,
                  chunkSizeBytes: end - start, uploadDurationMs: Date.now() - chunkStart,
                  attemptCount: attempt,
                });
                chunkProgress[progressKey] = end - start;
                return { partNumber: partRes.data.partNumber, etag: partRes.data.etag };
              } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError') throw err;
                console.warn(`Chunk ${partNum}/${totalParts} attempt ${attempt}/${maxRetries} failed:`, err.message);
                if (attempt === maxRetries) throw err;
                chunkProgress[progressKey] = 0;
                await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 1)));
              }
            }
          };
        });

        const allResults = await uploadChunksWithLimit(chunkJobs, maxConcurrent, abortController.signal);
        completedUploads.push({ key: uploadInfo.key, uploadId: uploadInfo.uploadId, parts: allResults });
      }

      clearTimeout(progressTimer);
      flushProgress();

      // Retry complete-upload independently — all chunks are in R2, losing this call loses the session
      for (let completeAttempt = 1; completeAttempt <= 3; completeAttempt++) {
        try {
          await axios.post(
            `${API_BASE}/sessions/${sessionId}/complete-upload`,
            { uploads: completedUploads },
            { timeout: completeTimeout, signal: abortController.signal }
          );
          break; // success
        } catch (completeErr) {
          if (completeErr.name === 'AbortError' || completeErr.name === 'CanceledError') throw completeErr;
          if (completeAttempt === 3) throw completeErr;
          console.warn(`[upload] complete-upload attempt ${completeAttempt}/3 failed: ${completeErr.message}, retrying...`);
          await new Promise(r => setTimeout(r, 2000 * Math.pow(2, completeAttempt - 1)));
        }
      }

      setUpload(prev => prev?.id === uploadId ? { ...prev, status: 'complete', progress: 100, eta: null, sessionId } : prev);
      const uploadDurationMs = Date.now() - uploadStartTimeRef.current;
      logUploadTelemetry({
        sessionId,
        fileCount: files.length,
        originalSizeBytes: totalSize,
        uploadDurationMs,
        uploadSpeedKbps: totalSize > 0 ? Math.round((totalSize / 1024) / (uploadDurationMs / 1000)) : null,
      });
      if (chunkTimings.length > 0) logChunkTelemetry(chunkTimings);
      toast.success('Upload complete! Analysis is running in the background.');
      setUploadInProgress(false);
      uploadLockRef.current = false;
      if (native) await disableBackgroundMode(true, 'Upload complete!', 'Analysis is running.');
      if (hasPendingLogout()) {
        clearPendingLogout();
        window.location.href = '/login';
      }
    } catch (error) {
      clearTimeout(progressTimer);
      uploadLockRef.current = false;
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        setUpload(prev => prev?.id === uploadId ? { ...prev, status: 'cancelled', progress: 0, eta: null } : prev);
        setUploadInProgress(false);
        if (native) await disableBackgroundMode(false);
        return;
      }

      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.response?.data?.error;
      const fileNames = files.map(f => f.name).join(', ');
      const totalSize = files.reduce((s, f) => s + f.size, 0);
      let logStage = 'upload-unknown';
      let logMsg = detail || error.message || 'Unknown error';
      let errorMsg = '';

      if (!navigator.onLine || error.message === 'Connection lost during upload') {
        logStage = 'connection-lost';
        logMsg = 'Internet connection lost during upload';
        errorMsg = 'No internet. Upload will resume automatically when you are back online.';
      } else if (error.code === 'ECONNABORTED') {
        logStage = 'upload-timeout';
        logMsg = `Upload timed out (${(totalSize / (1024 * 1024)).toFixed(1)} MB)`;
        errorMsg = 'Internet too slow. Upload will retry automatically.';
      } else if (status === 401) {
        logStage = 'auth-expired';
        logMsg = 'Session token expired during upload';
        errorMsg = 'Session expired. Please log in again.';
      } else if (status === 413) {
        logStage = 'server-file-too-large';
        logMsg = 'Server rejected: file too large';
        errorMsg = 'File is too large.';
      } else if (status === 429) {
        logStage = 'rate-limited';
        logMsg = detail || 'Daily upload limit reached';
        errorMsg = detail || 'Daily upload limit reached. Please try again tomorrow.';
      } else if (status === 400 && detail) {
        logStage = 'server-rejected';
        logMsg = detail;
        errorMsg = detail;
      } else if (!error.response) {
        logStage = 'server-unreachable';
        logMsg = `Cannot reach server: ${error.message}`;
        errorMsg = 'Cannot reach the server. Check your internet connection.';
      } else if (status >= 500) {
        logStage = 'server-error';
        logMsg = `Server ${status}: ${detail || error.message}`;
        errorMsg = 'Server error.';
      } else {
        errorMsg = detail || 'Upload failed';
      }

      setUpload(prev => prev?.id === uploadId ? { ...prev, status: 'failed', eta: null, error: errorMsg, failureStatus: status || null } : prev);
      toast.error(errorMsg);
      logUploadFailure({ stage: logStage, errorMessage: logMsg, fileName: fileNames, fileSizeBytes: totalSize });
      setUploadInProgress(false);
      if (native) await disableBackgroundMode(true, 'Upload failed', errorMsg);
      if (hasPendingLogout()) {
        clearPendingLogout();
        window.location.href = '/login';
      }
    }
  }, []);

  // ── Queue upload function (used by queue processor) ───────────────
  //
  // Signature: (fileBuffers, date, onProgress, stateManager) => Promise<sessionId>
  //
  // fileBuffers: Array<{ buffer: Blob, name, type, size }> with ._nativePaths and ._queueId
  // stateManager: { getState(), saveState(state) } for resumable uploads

  const directUploadForQueue = useCallback(async (fileBuffers, date, onProgress, stateManager, sessionType) => {
    const CHUNK_SIZE = 5 * 1024 * 1024;

    // Convert fileBuffers to File-like blobs
    const files = fileBuffers.map(fb => {
      const blob = fb.buffer instanceof Blob
        ? fb.buffer
        : new Blob([fb.buffer], { type: fb.type || 'application/octet-stream' });
      // Attach name/type/size for init-upload
      return Object.assign(blob, { name: fb.name, type: fb.type, size: fb.size });
    });

    // Network-adaptive values
    const chunkTimeoutMs  = getChunkTimeoutMs();
    const maxRetries      = getUploadRetryCount();
    const offlineWaitMs   = getOfflineWaitMs();
    const completeTimeout = getCompleteTimeoutMs();

    // Resume state
    let sessionId, uploads;
    const savedState = stateManager?.getState?.();
    if (savedState?.sessionId && savedState?.uploads) {
      sessionId = savedState.sessionId;
      uploads = savedState.uploads;
      console.log(`[queue-upload] Resuming session ${sessionId}`);
    } else {
      const initRes = await postInitUploadWithRetry(`${API_BASE}/sessions/init-upload`, {
        date,
        session_type: sessionType || 'evening_findings',
        files: files.map(f => ({
          name: f.name || 'audio.webm',
          type: f.type || 'application/octet-stream',
          size: f.size,
        })),
      }, { timeout: 30000 });
      sessionId = initRes.data.sessionId;
      uploads = initRes.data.uploads;
      if (!sessionId) {
        throw new Error('Server returned invalid response (missing sessionId)');
      }
      await stateManager?.saveState?.({ sessionId, uploads });
    }

    // Server says session is already processing — no upload needed
    if (!uploads || uploads.length === 0) {
      onProgress?.(100);
      return sessionId;
    }

    if (!Array.isArray(uploads)) {
      throw new Error('Server returned invalid response (malformed uploads)');
    }

    const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
    let bytesUploaded = 0;
    // Restore partially completed uploads from saved state
    const completedUploads = savedState?.completedUploads || [];
    const resumeFromFile = completedUploads.length; // skip files already fully uploaded
    const chunkTimings = [];

    for (let i = resumeFromFile; i < files.length; i++) {
      const file = files[i];
      const uploadInfo = uploads[i];
      if (!uploadInfo) {
        console.warn(`[queue-upload] Server returned ${uploads.length} upload slots for ${files.length} files — skipping file ${i + 1}`);
        if (i === 0) throw new Error('Server returned no upload slots');
        break;
      }

      if (uploadInfo.directPut) {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const online = await waitForOnline(offlineWaitMs);
          if (!online) throw new Error('Connection lost during upload');
          const chunkStart = Date.now();
          try {
            await axios.post(
              `${API_BASE}/sessions/${sessionId}/direct-put`,
              file,
              {
                headers: {
                  'Content-Type': 'application/octet-stream',
                  'X-Object-Key': uploadInfo.key,
                  'X-Content-Type': uploadInfo.contentType || file.type || 'application/octet-stream',
                },
                timeout: chunkTimeoutMs,
                onUploadProgress: (p) => {
                  const pct = Math.min(99, Math.round(((bytesUploaded + p.loaded) * 100) / Math.max(totalSize, 1)));
                  onProgress?.(pct);
                },
              }
            );
            chunkTimings.push({
              sessionId, uploadPath: 'queue-direct-put', chunkIndex: 0,
              chunkSizeBytes: file.size, uploadDurationMs: Date.now() - chunkStart,
              attemptCount: attempt,
            });
            bytesUploaded += file.size;
            break;
          } catch (err) {
            if (err.response?.status === 401) throw err;
            console.warn(`[queue-upload] direct-put attempt ${attempt}/${maxRetries}:`, err.message);
            if (attempt === maxRetries) throw err;
            await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 1)));
          }
        }
        completedUploads.push({ key: uploadInfo.key, directPut: true });
        await stateManager?.saveState?.({ sessionId, uploads, completedUploads });
        continue;
      }

      // Multipart — resume from saved parts if available
      const totalParts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      const savedParts = savedState?.currentFileParts || [];
      const parts = [...savedParts];
      const startFromPart = savedParts.length;

      // Account for already-uploaded bytes from resumed parts
      bytesUploaded += startFromPart * CHUNK_SIZE;

      for (let idx = startFromPart; idx < totalParts; idx++) {
        const partNum = idx + 1;
        const start = idx * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const online = await waitForOnline(offlineWaitMs);
          if (!online) throw new Error('Connection lost during upload');
          const chunkStart = Date.now();
          try {
            const partRes = await axios.post(
              `${API_BASE}/sessions/${sessionId}/upload-part`,
              chunk,
              {
                headers: {
                  'Content-Type': 'application/octet-stream',
                  'X-Object-Key': uploadInfo.key,
                  'X-Upload-Id': uploadInfo.uploadId,
                  'X-Part-Number': String(partNum),
                },
                timeout: chunkTimeoutMs,
                onUploadProgress: (p) => {
                  const pct = Math.min(99, Math.round(((bytesUploaded + p.loaded) * 100) / Math.max(totalSize, 1)));
                  onProgress?.(pct);
                },
              }
            );
            chunkTimings.push({
              sessionId, uploadPath: 'queue-multipart', chunkIndex: idx,
              chunkSizeBytes: end - start, uploadDurationMs: Date.now() - chunkStart,
              attemptCount: attempt,
            });
            bytesUploaded += (end - start);
            parts.push({ partNumber: partRes.data.partNumber, etag: partRes.data.etag });
            // Persist after each chunk so we can resume mid-file
            await stateManager?.saveState?.({ sessionId, uploads, completedUploads, currentFileParts: parts });
            break;
          } catch (err) {
            if (err.response?.status === 401) throw err;
            console.warn(`[queue-upload] chunk ${partNum}/${totalParts} attempt ${attempt}/${maxRetries}:`, err.message);
            if (attempt === maxRetries) throw err;
            await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt - 1)));
          }
        }
      }

      completedUploads.push({ key: uploadInfo.key, uploadId: uploadInfo.uploadId, parts });
      // Clear currentFileParts since this file is fully uploaded
      await stateManager?.saveState?.({ sessionId, uploads, completedUploads, currentFileParts: null });
    }

    // Retry complete-upload independently — all chunks are in R2, losing this call loses the session
    for (let completeAttempt = 1; completeAttempt <= 3; completeAttempt++) {
      try {
        await axios.post(
          `${API_BASE}/sessions/${sessionId}/complete-upload`,
          { uploads: completedUploads },
          { timeout: completeTimeout }
        );
        break; // success
      } catch (completeErr) {
        if (completeErr.response?.status === 401) throw completeErr;
        if (completeAttempt === 3) throw completeErr;
        console.warn(`[queue-upload] complete-upload attempt ${completeAttempt}/3 failed: ${completeErr.message}, retrying...`);
        await new Promise(r => setTimeout(r, 2000 * Math.pow(2, completeAttempt - 1)));
      }
    }

    await stateManager?.saveState?.(null); // clear on success
    onProgress?.(100);
    if (chunkTimings.length > 0) logChunkTelemetry(chunkTimings);
    return sessionId;
  }, []);

  // ── Public: enqueue upload ────────────────────────────────────────

  const enqueueUpload = useCallback(async (files, date, durationSeconds, sessionType) => {
    const uploadId = `upload-${Date.now()}`;

    if (!navigator.onLine) {
      await queueEnqueue(files, date, sessionType);
      await refreshQueue();
      toast.info('You are offline. Upload will start when you are back online.');
      return false;
    }

    setUpload({ id: uploadId, status: 'uploading', progress: 0, eta: null, files, date, durationSeconds, sessionType });
    runUpload(files, date, uploadId, durationSeconds, sessionType);
    return true;
  }, [runUpload, refreshQueue]);

  const cancelUpload = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const retryUpload = useCallback(() => {
    if (upload?.files && upload?.date) {
      const uploadId = `upload-${Date.now()}`;
      setUpload({ id: uploadId, status: 'uploading', progress: 0, eta: null, files: upload.files, date: upload.date, durationSeconds: upload.durationSeconds, sessionType: upload.sessionType });
      runUpload(upload.files, upload.date, uploadId, upload.durationSeconds, upload.sessionType);
    }
  }, [upload, runUpload]);

  const dismissUpload = useCallback(() => setUpload(null), []);

  // ── Auto-dismiss failed/completed banners after 30s ─────────────
  useEffect(() => {
    if (!upload || (upload.status !== 'failed' && upload.status !== 'complete')) return;
    const timer = setTimeout(() => setUpload(null), 30000);
    return () => clearTimeout(timer);
  }, [upload?.status, upload?.id]);

  // ── Queue management ─────────────────────────────────────────────

  const removeQueueItem = useCallback(async (id) => {
    await removeFromQueue(id);
    await refreshQueue();
  }, [refreshQueue]);

  const retryQueue = useCallback(async () => {
    await processQueue(directUploadForQueue);
    await refreshQueue();
  }, [refreshQueue, directUploadForQueue]);

  // ── Init: register queue upload fn + network listener ────────────

  useEffect(() => {
    setQueueUploadFn(directUploadForQueue);
    setupNetworkListener(directUploadForQueue);
    refreshQueue();
    const unsub = onQueueChange(() => refreshQueue());
    return () => unsub?.();
  }, []); // directUploadForQueue is stable (useCallback with no deps)

  // Process queue on mount (handles items left from previous session)
  // directUploadForQueue is stable (useCallback with no deps), so empty deps is correct
  // eslint-disable-next-line
  useEffect(() => {
    const t = setTimeout(() => processQueue(directUploadForQueue), 3000);
    return () => clearTimeout(t);
  }, []);

  // Periodic cleanup of stale queue entries
  useEffect(() => {
    const id = setInterval(() => cleanupCompleted().then(() => refreshQueue()), 60000);
    return () => clearInterval(id);
  }, [refreshQueue]);

  return (
    <UploadContext.Provider value={{
      upload,
      enqueueUpload,
      cancelUpload,
      retryUpload,
      dismissUpload,
      queueItems,
      queuePendingCount,
      queueUploading,
      removeQueueItem,
      retryQueue,
    }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) throw new Error('useUpload must be used within UploadProvider');
  return context;
};
